/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2025, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import { Component, Inject, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { TranslationService } from '../../../common/translation.service';
import { ButtonStyle } from '../../../aero/aero-button/aero-button.component';
import { BULB_BUSY_COVER_SERVICE } from '../../angularjs.providers';

interface CopyTemplateRequestDTO {
  /**
   * List of positions to copy.
   */
  positions: any[];

  /**
   * List of template names to copy.
   */
  templateName: any[];

  /**
   * List of grade levels to copy.
   */
  gradeLevels: any[];
}

interface CopyTemplateFailureDetail {
  /**
   * Account name associated with the failure.
   */
  accountName: string;

  /**
   * Failure message returned by the backend.
   */
  reason: string;
}

interface CopyTemplateHistoryDTO {
  /**
   * Unique id of the history entry.
   */
  id?: string;

  /**
   * Template path that was copied.
   */
  templatePath?: string;

  /**
   * Organization that initiated the copy.
   */
  organizationId?: string;

  /**
   * Positions selected for this copy.
   */
  positions?: string[];

  /**
   * Grade levels selected for this copy.
   */
  gradeLevels?: string[];

  /**
   * Current status of the copy request.
   */
  status?: string;

  /**
   * Per-account failure details.
   */
  failureDetails?: CopyTemplateFailureDetail[];

  /**
   * Number of successful account copies.
   */
  successCount?: number;

  /**
   * Number of failed account copies.
   */
  failedCount?: number;

  /**
   * Total number of processed accounts.
   */
  total?: number;

  /**
   * Account name that started the copy.
   */
  processedByName?: string;

  /**
   * Copy start timestamp.
   */
  startDate?: string;

  /**
   * Copy completion timestamp.
   */
  endDate?: string;
}

interface CopyTemplateHistoryViewModel extends CopyTemplateHistoryDTO {
  /**
   * Failure records expanded for the table view.
   */
  failureDetails: CopyTemplateFailureDetail[];

  /**
   * Human-friendly label for the status.
   */
  statusLabel: string;

}

/**
 * Component that loads when Org admin clicks copy button.
 */
@Component({
  selector: 'aero-copy-template-dialog',
  templateUrl: './copy-template-dialog.component.html',
  styleUrls: ['./copy-template-dialog.component.scss'],
  standalone: false,
})
export class CopyTemplateDialogComponent implements OnInit {

  /**
   * Default grey button style for the dialog.
   */
  aeroStyleGrey: ButtonStyle = ButtonStyle.GREY;

  /**
   * Holds any error message returned from the API.
   */
  errorMessage: string;

  /**
   * Holds any error message returned while loading the history tab.
   */
  historyErrorMessage: string;

  /**
   * Selected tab index in the dialog.
   */
  activeTabIndex = 0;

  /**
   * List of grade options shown in the dropdown.
   */
  gradeOptions = [];

  /**
   * Copy template history items returned from the backend.
   */
  historyItems: CopyTemplateHistoryViewModel[] = [];

  /**
   * Completed history items shown in the accordion.
   */
  completedHistoryItems: CopyTemplateHistoryViewModel[] = [];

  /**
   * Value of the currently expanded history panel.
   */
  expandedHistoryItem = '';

  /**
   * Input fields for template URLs or names.
   */
  inputs = [
    { value: '', type: 'text' },
  ];

  /**
   * List of position options shown in the dropdown.
   */
  positionOptions = [];

  /**
   * Currently selected grades.
   */
  selectedGrades = ['ALL'];

  /**
   * Currently selected positions.
   */
  selectedPositions = ['ALL'];

  /**
   * The previous position selection, used to identify whether All or an individual option was selected.
   */
  private previousSelectedPositions = ['ALL'];

  /**
   * The previous grade selection, used to identify whether All or an individual option was selected.
   */
  private previousSelectedGrades = ['ALL'];

  /**
   * Injecting required services.
   * @param config
   * @param dynamicDialogRef
   * @param $translate
   * @param translocoService
   * @param httpClient
   * @param bulbBusyCoverService
   */
  constructor(
    public config: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private $translate: TranslationService,
    private translocoService: TranslocoService,
    private httpClient: HttpClient,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService: any,
  ) {
  }

  /**
   * Initializes dropdown options for grade and position.
   */
  ngOnInit(): void {
    this.positionOptions = [
      { code: 'ALL', name: this.$translate.translateInstant('organization_copy_template_all') },
      { code: 'EDUCATOR', name: this.$translate.translateInstant('sign_up_bulb_position_educator') },
      { code: 'STUDENT', name: this.$translate.translateInstant('sign_up_bulb_position_student') },
      { code: 'PROFESSIONAL', name: this.$translate.translateInstant('sign_up_bulb_position_professional') },
      { code: 'FAMILYMEMBER', name: this.$translate.translateInstant('sign_up_bulb_position_parent_guardian') },
      { code: 'ADMINISTRATOR', name: this.$translate.translateInstant('sign_up_bulb_position_administrator') },
      { code: 'OTHER', name: this.$translate.translateInstant('sign_up_bulb_position_other') },
      {
        code: 'UNSPECIFIED',
        name: this.$translate.translateInstant('organization_copy_template_position_unspecified'),
      },
    ];

    this.gradeOptions = [
      { code: 'ALL', name: this.$translate.translateInstant('organization_copy_template_all') },
      { code: 'PRESCHOOL', name: this.$translate.translateInstant('organization_dashboard_preschool_letter') },
      {
        code: 'KINDERGARTEN',
        name: this.$translate.translateInstant('organization_dashboard_kindergarten_letter'),
      },
      { code: 'FIRST', name: '1' }, { code: 'SECOND', name: '2' },
      { code: 'THIRD', name: '3' }, { code: 'FOURTH', name: '4' },
      { code: 'FIFTH', name: '5' }, { code: 'SIXTH', name: '6' },
      { code: 'SEVENTH', name: '7' }, { code: 'EIGHTH', name: '8' },
      { code: 'NINTH', name: '9' }, { code: 'TENTH', name: '10' },
      { code: 'ELEVENTH', name: '11' }, { code: 'TWELFTH', name: '12' },
      { code: 'POSTGRADUATE', name: this.$translate.translateInstant('grade_level_POSTGRADUATE') },
      { code: 'OTHER', name: this.$translate.translateInstant('organization_dashboard_other_letters') },
      { code: 'UNASSIGNED', name: this.$translate.translateInstant('grade_level_UNASSIGNED') },
    ];

    this.loadCopyTemplateHistory();
  }

  /**
   * Loads completed template-copy history for the current organization.
   */
  private loadCopyTemplateHistory(): void {
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();
    this.httpClient.get<CopyTemplateHistoryDTO[]>(
      `organizations/${this.config.data.organizationId}/copyTemplate/history`,
    ).pipe(
      finalize(() => {
        stopBusyIndicatorCallback();
      }),
    ).subscribe({
      next: (historyItems) => {
        this.historyItems = (historyItems || []).map((historyItem) => this.toHistoryViewModel(historyItem));
        this.completedHistoryItems = this.historyItems;
        this.expandedHistoryItem = this.completedHistoryItems.length > 0
          ? this.getHistoryPanelValue(this.completedHistoryItems[0])
          : '';
        this.historyErrorMessage = '';
        if (this.activeTabIndex !== 0) {
          this.activeTabIndex = 0;
        }
      },
      error: () => {
        this.historyItems = [];
        this.completedHistoryItems = [];
        this.expandedHistoryItem = '';
        this.historyErrorMessage = 'Unable to load copy template history.';
      },
    });
  }

  /**
   * Converts the backend payload into a view model for the template.
   *
   * @param historyItem History payload from the backend.
   * @return The mapped view model.
   */
  private toHistoryViewModel(historyItem: CopyTemplateHistoryDTO): CopyTemplateHistoryViewModel {
    const status = historyItem.status || 'UNKNOWN';

    return {
      ...historyItem,
      failedCount: historyItem.failedCount || 0,
      failureDetails: historyItem.failureDetails || [],
      statusLabel: this.getHistoryStatusLabel(status),
      successCount: historyItem.successCount || 0,
      total: historyItem.total || 0,
    };
  }

  /**
   * Returns a user-friendly label for a history status value.
   *
   * @param status Backend status value.
   * @return Readable status text.
   */
  private getHistoryStatusLabel(status: string): string {
    switch (status) {
      case 'PROCESSING':
        return 'In progress';
      case 'SUCCESS':
        return 'Success';
      case 'FAILED':
        return 'Failed';
      case 'PARTIAL':
        return 'Partial';
      default:
        return status;
    }
  }

  /**
   * Builds the request data from form inputs and triggers template copy.
   */
  onCopyTemplate() {
    if (this.isButtonEnabled()) {
      const grades = this.selectedGrades.includes('ALL') ? [] : this.selectedGrades;
      const positions = this.selectedPositions.includes('ALL') ? [] : this.selectedPositions;
      const templates = this.inputs
        .map((input) => {
          const templateName = input.value.trim();
          if (templateName.startsWith('https://')) {
            // Must contain '/u/' in the path to be valid
            const match = templateName.match(/\/u\/([^\/\s]+)/);
            if (match) {
              return match[1];
            }
            // Invalid URL, return empty string to invalidate field
            this.errorMessage = this.translocoService.translate('copyTemplate.errorMessage');
            return '';
          }
          if (templateName.startsWith('http://')) {
            this.errorMessage = this.translocoService.translate('copyTemplate.errorMessage');
            return '';
          }
          // Accept `/u/abc`, `u/abc`, `/abc` and return `abc`
          const match = templateName.match(/^(\/?u\/|\/)([^\/\s]+)$/);
          if (match) {
            return match[2];
          }
          // If just a name, return as is
          return templateName;
        })
        .filter((templateName) => templateName.trim() !== '');

      const copyTemplateData: CopyTemplateRequestDTO = {
        templateName: templates,
        gradeLevels: grades,
        positions,
      };

      if (templates.length !== 0) {
        this.copyTemplate(copyTemplateData);
      }
    }
  }

  /**
   * Keeps the All position option mutually exclusive with individual positions.
   *
   * @param event Latest multi-select event.
   */
  onPositionsChange(selectedPositions: string[]): void {
    this.selectedPositions = CopyTemplateDialogComponent.normalizeAllSelection(selectedPositions, this.previousSelectedPositions);
    this.previousSelectedPositions = this.selectedPositions;
  }

  /**
   * Keeps the All grade option mutually exclusive with individual grades.
   *
   * @param event Latest multi-select event.
   */
  onGradesChange(selectedGrades: string[]): void {
    this.selectedGrades = CopyTemplateDialogComponent.normalizeAllSelection(selectedGrades, this.previousSelectedGrades);
    this.previousSelectedGrades = this.selectedGrades;
  }

  /**
   * Sends the copy template request to the backend.
   * Shows a busy indicator during the operation.
   */
  private copyTemplate(copyTemplateData: CopyTemplateRequestDTO) {
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();
    this.httpClient.patch(
      `organizations/${this.config.data.organizationId}/copyTemplate`,
      copyTemplateData,
      { responseType: 'text' },
    ).pipe(
      finalize(() => {
        stopBusyIndicatorCallback();
      }),
    ).subscribe(
      {
        next: () => {
          this.loadCopyTemplateHistory();
        },
        error: (err) => {
          const error = JSON.parse(err.error);
          this.errorMessage = error.developerMessage;
        },
      },
    );
  }

  /**
   * Returns a stable tracking key for a history row.
   *
   * @param index Row index.
   * @param historyItem History record.
   * @return The tracking key.
   */
  trackByHistoryItem(index: number, historyItem: CopyTemplateHistoryViewModel): string {
    return historyItem.id || historyItem.templatePath || `${index}`;
  }

  /**
   * Returns the panel value used by the history accordion for a single history entry.
   *
   * @param historyItem History record.
   * @return Stable accordion panel value.
   */
  getHistoryPanelValue(historyItem: CopyTemplateHistoryViewModel): string {
    return historyItem.id || historyItem.templatePath || historyItem.startDate || 'history';
  }

  /**
   * Will close the dialog.
   */
  closeDialog() {
    document.getElementsByTagName('p-dynamicdialog')[0].remove();
    this.dynamicDialogRef.close();
  }

  /**
   * Returns true if any input has a non-empty value.
   */
  isButtonEnabled(): boolean {
    return this.inputs.some((input) => input.value.trim() !== '');
  }

  /**
   * Adds another template input field.
   * Maximum of 10 inputs are allowed.
   */
  addTemplateField() {
    if (this.inputs.length < 10) {
      this.inputs = [...this.inputs, { value: '', type: 'text' }];
    }
  }

  /**
   * Normalizes a multi-select change so All never remains selected with individual options.
   *
   * @param event Latest multi-select event.
   * @return The normalized selected option values.
   */
  private static normalizeAllSelection(selectedValues: string[], previousValues: string[]): string[] {
    const normalizedValues = selectedValues || [];

    if (normalizedValues.length <= 1 || !normalizedValues.includes('ALL')) {
      return normalizedValues;
    }

    return previousValues.includes('ALL')
      ? normalizedValues.filter((value) => value !== 'ALL')
      : ['ALL'];
  }

  /**
   * Maps an array of selected option codes to their corresponding translated display names.
   * If no codes are selected, it defaults to returning a translated "All" string.
   *
   * @param selectedCodes - An array of selected option codes, or undefined.
   * @param options - The list of available options, where each option contains a `code` and a translated `name`.
   * @returns A comma-separated string of the translated names. Falls back to the raw code if a match is not found.
   */
  getDisplayValues(selectedCodes: string[] | undefined, options: any[]): string {
    // If the array is empty, null, or undefined, show the translated 'ALL' string
    if (!selectedCodes || selectedCodes.length === 0) {
      return this.$translate.translateInstant('organization_copy_template_all');
    }

    // Map the selected codes to their translated names, filter out any undefined matches, and join them
    return selectedCodes
      .map((code) => {
        const option = options.find((opt) => opt.code === code);
        return option ? option.name : code; // Fallback to raw code if not found
      })
      .filter((name) => !!name)
      .join(', ');
  }
}
